import React, { useState, useMemo, useEffect, Suspense } from "react";
import { formatUSD } from "../utils/formatters";
import { dropInactiveReps } from "../utils/inactiveReps";
import { useAuth } from "../context/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { useData } from "../hooks/useData";
import { useCurrencyMode } from "../context/CurrencyContext";
import { useOperationsAnalytics } from "../hooks/useOperationsAnalytics";
import { useMetricSamples } from "../hooks/useMetricSamples";
import { usePersistedState } from "../hooks/usePersistedState";
import ConfidentialTabs from "../components/dashboard/ConfidentialTabs";
import FinancialGraph from "../components/dashboard/FinancialGraph";
import TransactionLog from "../components/dashboard/TransactionLog";
import CashHero from "../components/dashboard/CashHero";
import SalesPerformanceCard from "../components/dashboard/SalesPerformanceCard";
import SelfSetPanel from "../components/dashboard/SelfSetPanel";
import RevivedLeadsPanel from "../components/dashboard/RevivedLeadsPanel";
import { isSelfSet } from "../utils/selfSet";
import { computeSelfSetMetrics } from "../utils/selfSetMetrics";
import { useLostLeadsAnalytics } from "../hooks/data-fetching/useLostLeadsAnalytics";
import { toOrgDateKey, getDateKeyInTZ } from "../lib/timezone";
import KPIGrid from "../components/dashboard/KPIGrid";
import MetricCard from "../components/dashboard/MetricCard";
import CompactMetricCard from "../components/dashboard/CompactMetricCard";
import SectionHeader from "../components/dashboard/SectionHeader";
import AscensionsModal from "../components/dashboard/AscensionsModal";
import ShowRateBreakdownModal from "../components/dashboard/ShowRateBreakdownModal";
// Heavy components lazy-loaded to split mount work across frames
const SankeyFunnel = React.lazy(() => import("../components/funnel/SankeyFunnel"));
import ForecastTab from "../components/dashboard/ForecastTab";
import YearOverviewTab from "../components/dashboard/YearOverviewTab";
import InstallmentsTracker from "../components/dashboard/InstallmentsTracker";
const LeaderboardPodium = React.lazy(() => import("../components/dashboard/LeaderboardPodium"));
import SetterSnippet from "../components/dashboard/SetterSnippet";
import PerformanceSnippet from "../components/dashboard/PerformanceSnippet";
import UpcomingCallsSnippet from "../components/dashboard/UpcomingCallsSnippet";
import {
  DashboardHeader,
  ClientStats,
  CloserStats,
  SetterStats,
  DialerStats,
  TeamRoster,
  LeaderboardCard,
} from "../components/dashboard/sections";
// MultiPartnerProgress moved to MastermindDashboard (Exec Control Center)
import type { DashboardTab } from "../components/dashboard/sections";
import { useInstallmentData } from "../hooks/data-fetching";
import { useFunnelHealth } from "../hooks/useFunnelHealth";
import { useForecastData } from "../hooks/useForecastData";
import { useLeaderboardSettings } from "../hooks/useLeaderboardSettings";
import { useMonthlyGoal } from "../hooks/useMonthlyGoal";
// useSufferingSnippet removed — replaced by SetterSnippet in bento grid
import { useUpcomingCallsSnippet } from "../hooks/useUpcomingCallsSnippet";
import { roleConfig } from "../config/roleConfig";
import type { DashboardSection, UserRole } from "../types/core";

// =============================================================================
// OPERATIONS DASHBOARD — Config-Driven Role-Based Rendering
// =============================================================================
// PURPOSE: Role-specific dashboard with 40+ tracked metrics, revenue/cash KPIs,
// ticket breakdowns, and real-time data synchronization.
//
// ARCHITECTURE:
// Dashboard sections are composed based on roleConfig[role].dashboard — a
// declarative list of section keys defined in config/roleConfig.ts. Each key
// maps to a React element in the sectionMap below. To change what a role sees,
// edit roleConfig.ts — no code changes needed here.
//
// REQUIREMENTS:
// - All queries MUST filter by organization_id (multi-tenant)
// - Timestamps stored in UTC, converted with AT TIME ZONE org.timezone
// - All writes go through N8N webhooks (never direct Supabase inserts)
// - Pre-load data with 5-min TTL cache to prevent reloads while working
// - Show sync indicator when new data available (poll every 30s)
// =============================================================================

const OperationsDashboard: React.FC = () => {
  // currencyMode is a dep of the stageMetrics useMemo below (which pre-formats
  // money) so it recomputes on a USD⇄AUD flip; also re-renders this page in place.
  const currencyMode = useCurrencyMode();
  const { user } = useAuth();
  const {
    hasTunnelVision,
    showForecastTab,
    canEditLeaderboard,
    useBentoLayout,
    canSeeLostLeadsAnalytics,
  } = useRBAC();
  const { currentOrganization, teamMembers, leaderboard: rawLeaderboard } = useData();
  // Inactive reps never render on rep-facing podiums/snippets (Jul-3 roster doctrine).
  const leaderboard = useMemo(
    () => dropInactiveReps(rawLeaderboard, teamMembers),
    [rawLeaderboard, teamMembers]
  );
  const {
    kpis,
    dateRange,
    setDateRange,
    navigateDateRange,
    dailyChartData,
    chartGranularity,
    filteredTransactions,
    ascensionLeads,
  } = useOperationsAnalytics();
  // P-PERF.4: progressive-render phase gate, declared early so below-fold
  // fetches can defer on it (scheduler effect lives lower in the component).
  const [renderPhase, setRenderPhase] = useState(0);
  // P-PERF.4: installments render only in Phase 3 (below fold) — defer the
  // get_payment_plan_status fetch off the cold-load critical path.
  const { installmentLeads } = useInstallmentData(
    renderPhase >= 3 ? currentOrganization?.id : undefined
  );
  const {
    merged: funnelMerged,
    bySource: funnelBySource,
    returningDistribution,
    returningByDate,
    isLoading: funnelLoading,
  } = useFunnelHealth(currentOrganization?.id, dateRange);

  // Transform funnelBySource into format expected by SankeyFunnel
  const sankeyFunnelData = useMemo(() => {
    const toSourceMetrics = (data: (typeof funnelBySource)[string] | undefined) => ({
      leads_in: data?.leads_in ?? 0,
      booked: data?.booked ?? 0,
      shown: data?.shown ?? 0,
      closed: data?.closed ?? 0,
      low_ticket: data?.low_ticket ?? 0,
      revenue: data?.revenue ?? 0,
      cash: data?.cash ?? 0,
      // Source-specific splits (passed through for DetailView)
      qualified: data?.qualified,
      dq: data?.dq,
      dq_rate: data?.dq_rate,
      // Typeform dual-grain (P6, May 2026): submissions + lead counts
      applied_submissions: data?.applied_submissions,
      applied_leads: data?.applied_leads,
      qualified_submissions: data?.qualified_submissions,
      qualified_leads: data?.qualified_leads,
      dq_submissions: data?.dq_submissions,
      dq_leads: data?.dq_leads,
      attended: data?.attended,
      missed: data?.missed,
      captured: data?.captured,
      total_signups: data?.total_signups,
      unique_leads: data?.unique_leads,
      // LT exit classification
      lt_dq_exit: data?.lt_dq_exit,
      lt_dialer_exit: data?.lt_dialer_exit,
      lt_downsell_exit: data?.lt_downsell_exit,
      // Attribution indicator
      attributed_from_prior: data?.attributed_from_prior,
      // Pre-calls and cancellations
      pre_calls_filled: data?.pre_calls_filled,
      missing_precalls: data?.missing_precalls,
      cancellations: data?.cancellations,
      // Webinar sub-columns
      webinar_discovery_booked: data?.webinar_discovery_booked,
      webinar_discovery_shown: data?.webinar_discovery_shown,
      webinar_discovery_closed: data?.webinar_discovery_closed,
      webinar_discovery_revenue: data?.webinar_discovery_revenue,
      webinar_discovery_cash: data?.webinar_discovery_cash,
      webinar_upsell_booked: data?.webinar_upsell_booked,
      webinar_upsell_shown: data?.webinar_upsell_shown,
      webinar_upsell_closed: data?.webinar_upsell_closed,
      webinar_upsell_revenue: data?.webinar_upsell_revenue,
      webinar_upsell_cash: data?.webinar_upsell_cash,
      // Typeform sub-columns
      typeform_qual_booked: data?.typeform_qual_booked,
      typeform_qual_shown: data?.typeform_qual_shown,
      typeform_qual_closed: data?.typeform_qual_closed,
      typeform_revived_booked: data?.typeform_revived_booked,
      typeform_revived_shown: data?.typeform_revived_shown,
      typeform_revived_closed: data?.typeform_revived_closed,
    });

    const webinar = toSourceMetrics(funnelBySource?.webinar);
    const typeform = toSourceMetrics(funnelBySource?.typeform);
    const dm = toSourceMetrics(funnelBySource?.dm);

    return {
      webinar,
      typeform,
      dm,
      // Use server-provided merged totals (unfiltered) instead of summing per-source.
      // Per-source numbers are filtered (e.g., webinar only counts webinar-calendar bookings),
      // so summing them would undercount. Merged totals reflect ALL pipeline activity.
      totals: {
        leads_in: funnelMerged.leads_in,
        booked: funnelMerged.booked,
        shown: funnelMerged.shown,
        closed: funnelMerged.closed,
        low_ticket: funnelMerged.low_ticket,
        revenue: funnelMerged.revenue,
        cash: funnelMerged.cash,
      },
      returningDistribution,
      returningByDate,
    };
  }, [funnelBySource, funnelMerged, returningDistribution, returningByDate]);

  // Stage metrics for full-width funnel view (matching image 18 layout).
  // S51 (May 15, 2026): labels SSOT-aligned even though SankeyFunnel currently
  // doesn't render this prop (renders activeData per-source instead). Keep
  // aligned for the eventual wire-up — drift here causes silent label rot.
  const stageMetrics = useMemo(
    () => ({
      leads_in: [
        { label: "New Chats", value: kpis.newDMChats.toLocaleString() },
        { label: "Conversations", value: kpis.deepConversations.toLocaleString() },
      ],
      booked: [
        { label: "Setter Booked", value: kpis.setterBookedCalls.toLocaleString() },
        { label: "Pre-Calls", value: (funnelMerged.pre_calls_filled ?? 0).toLocaleString() },
        // Use gated kpis.cancellations (pre-call exists + no SCO) — matches Call
        // Summary tile so the dashboard never shows two different cancellation counts.
        { label: "Cancellations", value: kpis.cancellations.toLocaleString() },
      ],
      shown: [
        { label: "Shows", value: kpis.shows.toLocaleString() },
        { label: "Show Rate", value: `${kpis.showUpRate.toFixed(0)}%` },
        { label: "No-Shows", value: kpis.noShows.toLocaleString() },
      ],
      closed: [
        { label: "Deals", value: kpis.dealsClosed.toLocaleString() },
        { label: "Close Rate", value: `${kpis.closeRate.toFixed(0)}%` },
        { label: "Avg Cash / Close", value: formatUSD(kpis.aov) },
        { label: "Cash / Show", value: formatUSD(kpis.cashPerCallTaken) },
      ],
    }),
    [kpis, funnelMerged, currencyMode]
  );

  // Dynamic comparison period label shown once at section header level
  // D5/D7: Hidden on All Time — compare window would be 730d before GHL data exists → "−100%" noise
  const comparisonLabel = useMemo(() => {
    if (dateRange.isAllTime) return null;
    if (!dateRange.start || !dateRange.end) return "vs prior period";
    const days = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    );
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: currentOrganization?.timezone || "UTC",
      });
    const prevEnd = new Date(dateRange.start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - days + 1);
    return `vs last ${days} days (${fmt(prevStart)} – ${fmt(prevEnd)})`;
  }, [dateRange.isAllTime, dateRange.start, dateRange.end, currentOrganization?.timezone]);

  const [showAscensions, setShowAscensions] = useState(false);
  const [showBreakdownOpen, setShowBreakdownOpen] = useState(false);
  const [activeTab, setActiveTab] = usePersistedState<DashboardTab>(
    "altara_ops_activeTab",
    "performance"
  );
  const [selectedYear, setSelectedYear] = usePersistedState<number>(
    "altara_ops_yearTab_year",
    new Date().getFullYear()
  );
  const [yearTabDayRange, setYearTabDayRange] = usePersistedState<{ start: number; end: number }>(
    "altara_ops_yearTab_dayRange",
    { start: 1, end: 31 }
  );

  const role: UserRole = (user?.role as UserRole) || "viewer";

  // Forecast data — fetched for all roles (powers goal progress bar + forecast tab)
  const forecastData = useForecastData(currentOrganization?.id ?? null);
  const timezone = currentOrganization?.timezone || "UTC";

  const { getSamples } = useMetricSamples(
    filteredTransactions,
    leaderboard,
    ascensionLeads,
    timezone
  );

  // Monthly revenue goal — fetched for ALL roles (progress bar)
  const { goal: monthlyGoal } = useMonthlyGoal(
    currentOrganization?.id,
    undefined,
    currentOrganization?.timezone ?? null
  );

  // Leaderboard competition mode settings (threshold tiers for solo-rep roles)
  const {
    getMode,
    getThresholds,
    saveSetting: saveLeaderboardSetting,
  } = useLeaderboardSettings(currentOrganization?.id);
  // canEditLeaderboard comes from useRBAC

  // goalProgress moved into RevenueBarChart — goal bar now lives inside the hero card

  // Compute overdue count for installment badge
  const overdueCount = useMemo(
    () => installmentLeads.filter((l) => l.isOverdue).length,
    [installmentLeads]
  );

  // Pre-compute SCO transactions list — pulled out of sectionMap to avoid
  // redundant .filter() passes on every sectionMap recompute. The dynamic
  // OutcomeSummaryStrip (variant='compact', rendered inside TransactionLog
  // snippet mode) computes per-bucket counts internally from this list, so
  // no upstream summary aggregation is needed here.
  const { scoTransactions } = useMemo(() => {
    const sco = filteredTransactions.filter((t) => t.eventType === "sales_call_outcome");
    return { scoTransactions: sco };
  }, [filteredTransactions]);

  // ── SELF-SET FILTER ────────────────────────────────────────────────────────
  // Deliberately NOT persisted. A filter that changes every number on the page and
  // silently survives a reload is how someone reads a self-set slice as org-wide
  // truth. It resets to "all" on every mount; the toggle is one click away.
  const [selfSetOnly, setSelfSetOnly] = useState(false);

  // Computed from rows already in TransactionContext (paginated past the PGRST cap),
  // so this needs no RPC param and therefore cannot half-ship against an unapplied
  // migration. See utils/selfSetMetrics.ts for why that constraint drove the design.
  const selfSetMetrics = useMemo(
    () => computeSelfSetMetrics(filteredTransactions),
    [filteredTransactions]
  );

  // The log's rows in self-set mode. `isSelfSet` gates on event_type itself, so this
  // also drops bookings/cancellations/pre-calls — correct: none of them are self-set
  // deals, they merely carry a NULL setter by construction.
  const selfSetTransactions = useMemo(
    () => (selfSetOnly ? filteredTransactions.filter(isSelfSet) : filteredTransactions),
    [selfSetOnly, filteredTransactions]
  );

  // ── REVIVED LOST LEADS ─────────────────────────────────────────────────────
  // Same SQL function as the Lost Leads "Analytics" tab (get_lost_leads_analytics),
  // so the card and the tab cannot disagree. Range = CLAIM date in org tz (Mako
  // 2026-10-01). Rule = /lostlead: claimer dialed + a call booked after the claim.
  // Replaced the 30-day client-side slice (utils/revivedMetrics.ts) on 2026-10-01.
  const revivedFrom =
    dateRange.isAllTime || !dateRange.start ? null : toOrgDateKey(dateRange.start);
  const revivedTo = dateRange.isAllTime || !dateRange.end ? null : toOrgDateKey(dateRange.end);
  const revivedToday = getDateKeyInTZ(new Date().toISOString(), timezone);
  const {
    data: lostLeadsAnalytics,
    loading: revivedLoading,
    error: revivedError,
  } = useLostLeadsAnalytics(
    currentOrganization?.id ?? null,
    revivedFrom,
    revivedTo,
    revivedToday,
    // Only while the card can render (self-set view) and for roles the server allows.
    // Org-wide numbers: showing them to a tunnel-vision rep would leak the org.
    selfSetOnly && canSeeLostLeadsAnalytics
  );

  // Exec and manager get bento grid layout (useBentoLayout from useRBAC)
  const useBentoGrid = useBentoLayout;

  // Progressive rendering — phased across multiple animation frames.
  // Phase 0: Hero only (instant visual feedback)
  // Phase 1: Funnel + Sales Performance (next frame)
  // Phase 2: Leaderboards (frame after)
  // Phase 3: Activity zone — transactions, installments, upcoming calls (final frame)
  // This breaks a single 2.4s long task into ~4 smaller tasks under 500ms each.
  // (renderPhase declared near the top — moved up for the P-PERF.4 installment deferral.)
  useEffect(() => {
    let cancelled = false;
    const scheduleNext = (phase: number) => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        setRenderPhase(phase);
        if (phase < 3) {
          scheduleNext(phase + 1);
        }
      });
    };
    // Start phase 1 after first paint
    requestAnimationFrame(() => {
      if (!cancelled) scheduleNext(1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Bento-only data (upcoming calls) — skip 3-query waterfall for tunnel vision roles ──
  const {
    upcomingCalls,
    totalUpcoming,
    isLoading: callsLoading,
  } = useUpcomingCallsSnippet(useBentoGrid ? currentOrganization?.id : undefined, timezone);

  // ─────────────────────────────────────────────────────────────────────────
  // Section Map: maps DashboardSection keys → React elements
  // roleConfig[role].dashboard determines which keys render and in what order.
  //
  // TIER 1: hero — Revenue Closed + Cash Collected (new HeroCard components)
  // TIER 2: salesPerformance — AOV, Cash/Call, Deals (MetricCard grid)
  // TIER 3: conversionQuality — Close Rate, Show-up Rate (MetricCard grid)
  // TIER 4: pipelineOutreach — Setter Booked, Calls, DMs (CompactMetricCard grid)
  // COLLAPSIBLE: ticketBreakdown, installments, transactions
  // LEGACY: kpiGrid, financialGraph (still used by manager/viewer roles)
  // ─────────────────────────────────────────────────────────────────────────
  const sectionMap = useMemo(
    (): Record<DashboardSection, React.ReactNode> => ({
      // ── TIER 1: Hero Bento — Rev/Cash bars (with breakdown) + Chart ────
      hero: (
        <CashHero
          orgId={currentOrganization?.id ?? null}
          dateRange={dateRange}
          goalAmount={monthlyGoal?.revenue_goal}
          daysElapsed={forecastData.currentMonthMetrics.days_elapsed}
          daysInMonth={forecastData.currentMonthMetrics.days_in_month}
          getSamples={getSamples}
        />
      ),

      // ── FUNNEL HEALTH: Full-width funnel with stage-aligned metrics ────
      funnelHealth: (
        <Suspense fallback={<div className="liquid-glass p-4 min-h-[200px] anim-skeleton" />}>
          {/* The funnel is fed by get_funnel_metrics / get_operations_kpis — server
              aggregates that take no self-set parameter, so it stays ORG-WIDE while the
              rest of the page is filtered. Say so out loud. An unlabelled org-wide funnel
              sitting under an active "Self-Set ✓" toggle is a number that lies by
              omission, which is the failure this page has already shipped once. */}
          {selfSetOnly && (
            <div
              className="mb-2 flex items-start gap-2 rounded-2xl border border-black/[0.06] bg-black/[0.02] px-3 py-2"
              data-testid="funnel-orgwide-notice"
            >
              <span className="token-11 text-ds-amber shrink-0">Org-wide</span>
              <span className="token-11 text-glass-muted">
                The funnel is not filtered by the Self-Set toggle. Self-set deals have no
                source-attributed path — the closer sourced the lead directly, with no pre-call
                behind it — so Leads In and Booked have no self-set equivalent. Self-set numbers are
                in the panel below.
              </span>
            </div>
          )}
          <SankeyFunnel
            data={sankeyFunnelData}
            stageMetrics={stageMetrics}
            isLoading={funnelLoading}
            fullWidth
            dmAnalytics={{
              totalCreated: kpis.totalConvosCreated,
              newDMChats: kpis.newDMChats,
              engaged: kpis.engaged,
              conversations: kpis.conversations,
              // responseRate intentionally omitted — SSOT-PARKED metric
              // (metricMeta.ts:766). SankeyFunnel guards on `!= null`.
              deepConversations: kpis.deepConversations,
            }}
          />
        </Suspense>
      ),

      // ── UNIFIED PERFORMANCE CARD: Sales, Calls, DMs in one big glass card ──
      // Extracted to <SalesPerformanceCard> May 1 (SSOT — same component renders /admin/portal).
      // Self-set mode swaps this card for the closer-sourced slice. A swap, not an
      // overlay: SalesPerformanceCard's numbers come from the org-wide RPCs and cannot
      // be filtered client-side, so leaving it mounted next to a "Self-Set" toggle
      // would present org-wide figures as if they were the filtered ones.
      salesPerformance: selfSetOnly ? (
        // Two source slices stacked, not one merged card. `.bento-metrics` is a
        // stretch-flex column, so the row grows to fit rather than clipping.
        // The inner wrapper div is load-bearing: SelfSetPanel sets `h-full`, which
        // against an auto-height parent resolves to auto — without it, two h-full
        // siblings in one flex column both claim the full height and overflow.
        // `rangeLabel` is deliberately NOT passed. It reads as a range echo, but the
        // only label available here is `comparisonLabel` — the PRIOR-period string
        // ("vs last 31 days (May 31 – Jun 30)"). Neither panel renders a delta, so
        // stamping that on them claimed a comparison that isn't being made: July
        // numbers captioned as if measured against June. The date-range picker at the
        // top of the page already states the real window.
        <div className="flex flex-col gap-4 h-full" data-testid="source-breakdown">
          <div>
            <SelfSetPanel metrics={selfSetMetrics} />
          </div>
          {canSeeLostLeadsAnalytics && (
            <RevivedLeadsPanel
              revived={lostLeadsAnalytics?.revived_totals ?? null}
              claimsInRange={lostLeadsAnalytics?.totals?.claims ?? 0}
              loading={revivedLoading}
              error={revivedError}
            />
          )}
        </div>
      ) : (
        <SalesPerformanceCard
          kpis={kpis}
          funnelMerged={funnelMerged}
          showPreCallOwnerSplit
          comparisonLabel={comparisonLabel}
          getSamples={getSamples}
          onShowUpRateClick={() => setShowBreakdownOpen(true)}
        />
      ),

      // DEPRECATED 2026-04-28 — duplicates Sales & Conversion section's Show-up Rate + Close Rate.
      // Plan deletion 2026-05-28 if no role config or external consumer surfaces by then.
      // Audit: zero hits in lib/roleConfig.ts, single render site here.
      conversionQuality: (
        <SectionHeader title="Conversion & Quality">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <MetricCard
              label="Close Rate"
              value={`${kpis.closeRate.toFixed(1)}%`}
              change={kpis.closeRateChange}
              metricKey="closeRate"
              getSamples={getSamples}
            />
            <MetricCard
              label="Show-up Rate"
              value={`${kpis.showUpRate.toFixed(1)}%`}
              change={kpis.showUpRateChange}
              metricKey="showUpRate"
              getSamples={getSamples}
              onClick={() => setShowBreakdownOpen(true)}
            />
            {/* Funnel Effectiveness removed — non-inferable label */}
          </div>
        </SectionHeader>
      ),

      // ── CALL PIPELINE + CASH YIELD: legacy section for roles that use it
      // separately. Option B (S51 May 15 PM): replaced single 6-tile "Call Summary"
      // grid with a 4-tile horizontal pipeline strip (lead's funnel journey →) and
      // a separate 2-tile yield row. Reschedules dropped from primary view — they
      // count as shows per SSOT, not as a third call-outcome lane. Add it back as
      // a third yield tile if rep coaching needs it.
      pipelineOutreach: (
        <div className="space-y-3">
          <SectionHeader title="Call Pipeline">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <CompactMetricCard
                label="Pre-Calls"
                value={(funnelMerged.pre_calls_filled ?? 0).toLocaleString()}
                metricKey="preCallsMade"
                getSamples={getSamples}
              />
              <CompactMetricCard
                label="Cancellations"
                value={kpis.cancellations.toString()}
                metricKey="cancellations"
                getSamples={getSamples}
              />
              <CompactMetricCard
                label="Shows"
                value={kpis.shows.toString()}
                sub={kpis.callsBooked > 0 ? `${kpis.showUpRate.toFixed(0)}% show-up` : undefined}
                metricKey="shows"
                getSamples={getSamples}
              />
              <CompactMetricCard
                label="No-Shows"
                value={kpis.noShows.toString()}
                sub={
                  kpis.noShows > 0
                    ? `${kpis.scoNoShows} call · ${kpis.orphanPreCalls} orphan`
                    : undefined
                }
                metricKey="noShows"
                getSamples={getSamples}
              />
            </div>
          </SectionHeader>
          <SectionHeader title="Cash Yield">
            <div className="grid grid-cols-2 gap-2.5">
              <CompactMetricCard
                label="Cash / Show"
                value={formatUSD(kpis.cashPerCallTaken)}
                metricKey="cashPerCallTaken"
                getSamples={getSamples}
              />
              <CompactMetricCard
                label="Cash / Pre-Call"
                value={formatUSD(
                  funnelMerged.pre_calls_filled && funnelMerged.pre_calls_filled > 0
                    ? kpis.cashCollected / funnelMerged.pre_calls_filled
                    : 0
                )}
                metricKey="cashPerPreCall"
                getSamples={getSamples}
              />
            </div>
          </SectionHeader>
        </div>
      ),

      // ── DM HEALTH: Legacy section for roles that use it separately ──
      dmHealth: (
        <SectionHeader title="DM Health">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5">
            <CompactMetricCard label="New Chats" value={kpis.newDMChats.toString()} />
            <CompactMetricCard
              label="Conversations"
              value={kpis.deepConversations.toString()}
              sub={
                kpis.newDMChats > 0
                  ? `${((kpis.deepConversations / kpis.newDMChats) * 100).toFixed(0)}% conversion`
                  : undefined
              }
            />
            <CompactMetricCard
              label="Setter Booked"
              value={kpis.setterBookedCalls.toString()}
              sub={
                kpis.deepConversations > 0
                  ? `${((kpis.setterBookedCalls / kpis.deepConversations) * 100).toFixed(0)}% of convos`
                  : undefined
              }
            />
          </div>
        </SectionHeader>
      ),

      // ── Cash & Revenue chart (HT/LT/Inst breakdown now in bento grid above) ──
      ticketBreakdown: (
        <SectionHeader title="Cash & Revenue">
          <FinancialGraph data={dailyChartData} granularity={chartGranularity} />
        </SectionHeader>
      ),

      // ── Installments ─────────────────────────────────────
      installments: (
        <div className="liquid-glass anim-lift p-4 h-full">
          <SectionHeader
            title="Installment Tracking"
            badge={
              <div className="flex items-center gap-3">
                {overdueCount > 0 && (
                  <span
                    className="token-11 font-semibold px-2 py-0.5 rounded-md"
                    style={{ color: "var(--red)", backgroundColor: "var(--red-bg)" }}
                  >
                    {overdueCount} overdue
                  </span>
                )}
                <a href="#/admin/installments" className="token-11 text-ds-accent hover:underline">
                  View all →
                </a>
              </div>
            }
          >
            <InstallmentsTracker
              leads={installmentLeads}
              snippet
              timezone={timezone}
              viewAllLink="/admin/installments"
              viewAllCount={installmentLeads.length}
            />
          </SectionHeader>
        </div>
      ),

      // ── Transactions ─────────────────────────────────────
      // scoTransactions pre-computed above in its own useMemo
      transactions: (
        <div className="liquid-glass anim-lift p-4 h-full">
          <SectionHeader
            title={selfSetOnly ? "Transactions · Self-Set" : "Transactions"}
            badge={
              <div className="flex items-center gap-3">
                <span className="token-11 text-glass-muted">
                  {selfSetOnly
                    ? `${selfSetMetrics.outcomeRows} self-set`
                    : `${scoTransactions.length} total`}
                </span>
                <a href="#/admin/transactions" className="token-11 text-ds-accent hover:underline">
                  View all →
                </a>
              </div>
            }
          >
            <TransactionLog
              transactions={selfSetTransactions}
              snippet
              timezone={timezone}
              viewAllLink={
                (selfSetOnly ? selfSetMetrics.outcomeRows : scoTransactions.length) > 7
                  ? `/admin/transactions`
                  : undefined
              }
              viewAllCount={selfSetOnly ? selfSetMetrics.outcomeRows : scoTransactions.length}
            />
          </SectionHeader>
        </div>
      ),

      leaderboard: useBentoGrid ? (
        <Suspense fallback={<div className="anim-skeleton min-h-[100px]" />}>
          <LeaderboardPodium
            leaderboard={leaderboard}
            dateRange={dateRange}
            canEditSettings={canEditLeaderboard}
            getMode={getMode}
            getThresholds={getThresholds}
            onSaveSetting={saveLeaderboardSetting}
            organizationId={currentOrganization?.id}
          />
        </Suspense>
      ) : (
        <LeaderboardCard />
      ),

      leaderboardCloser: (
        <Suspense fallback={null}>
          <LeaderboardPodium
            leaderboard={leaderboard}
            dateRange={dateRange}
            canEditSettings={canEditLeaderboard}
            getMode={getMode}
            getThresholds={getThresholds}
            onSaveSetting={saveLeaderboardSetting}
            organizationId={currentOrganization?.id}
            sector="closer"
            bare
          />
        </Suspense>
      ),

      leaderboardSetter: (
        <Suspense fallback={null}>
          <LeaderboardPodium
            leaderboard={leaderboard}
            dateRange={dateRange}
            canEditSettings={canEditLeaderboard}
            getMode={getMode}
            getThresholds={getThresholds}
            onSaveSetting={saveLeaderboardSetting}
            organizationId={currentOrganization?.id}
            sector="setter"
            bare
          />
        </Suspense>
      ),

      leaderboardDialer: (
        <Suspense fallback={null}>
          <LeaderboardPodium
            leaderboard={leaderboard}
            dateRange={dateRange}
            canEditSettings={canEditLeaderboard}
            getMode={getMode}
            getThresholds={getThresholds}
            onSaveSetting={saveLeaderboardSetting}
            organizationId={currentOrganization?.id}
            sector="dialer"
            bare
          />
        </Suspense>
      ),

      sufferingLeads: <SetterSnippet leaderboard={leaderboard} dateRange={dateRange} />,

      perfSetter: (
        <PerformanceSnippet leaderboard={leaderboard} dateRange={dateRange} sector="setter" bare />
      ),

      perfCloser: (
        <PerformanceSnippet leaderboard={leaderboard} dateRange={dateRange} sector="closer" bare />
      ),

      perfDialer: (
        <PerformanceSnippet leaderboard={leaderboard} dateRange={dateRange} sector="dialer" bare />
      ),

      upcomingCalls: (
        <UpcomingCallsSnippet
          calls={upcomingCalls}
          totalCount={totalUpcoming}
          isLoading={callsLoading}
        />
      ),

      team: <TeamRoster teamMembers={teamMembers} />,

      confidential: <ConfidentialTabs />,

      // ── LEGACY: still used by viewer/manager roles ────────────────────
      financialGraph: <FinancialGraph data={dailyChartData} granularity={chartGranularity} />,

      kpiGrid: <KPIGrid kpis={kpis} getSamples={getSamples} />,

      // ── ROLE-SPECIFIC sections ────────────────────────────────────────
      clientStats: <ClientStats kpis={kpis} getSamples={getSamples} />,

      closerStats: <CloserStats kpis={kpis} getSamples={getSamples} />,

      setterStats: <SetterStats kpis={kpis} getSamples={getSamples} />,

      dialerStats: <DialerStats kpis={kpis} getSamples={getSamples} />,
    }),
    [
      kpis,
      getSamples,
      dailyChartData,
      chartGranularity,
      // filteredTransactions dropped: sectionMap now reads selfSetTransactions, which is
      // itself derived from it, so reactivity is preserved without the redundant dep.
      installmentLeads,
      overdueCount,
      teamMembers,
      timezone,
      sankeyFunnelData,
      stageMetrics,
      funnelLoading,
      leaderboard,
      dateRange,
      useBentoGrid,
      canEditLeaderboard,
      getMode,
      getThresholds,
      saveLeaderboardSetting,
      currentOrganization?.id,
      upcomingCalls,
      totalUpcoming,
      callsLoading,
      // renderPhase intentionally excluded — sectionMap computes all sections,
      // rendering is gated in JSX below to spread work across frames
      scoTransactions,
      funnelMerged,
      comparisonLabel,
      forecastData.currentMonthMetrics,
      monthlyGoal?.revenue_goal,
      selfSetOnly,
      selfSetMetrics,
      selfSetTransactions,
      canSeeLostLeadsAnalytics,
      lostLeadsAnalytics,
      revivedLoading,
      revivedError,
    ]
  );

  // Get ordered section keys for the current role
  const sections = roleConfig[role].dashboard;
  if (!currentOrganization) {
    return <div className="p-8 text-center text-glass-muted">Please select an organization.</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3 pb-24 overflow-hidden">
      <div className="animate-rise animate-rise-1">
        <DashboardHeader
          organizationName={currentOrganization.name}
          orgTimezone={currentOrganization.timezone ?? undefined}
          activeTab={showForecastTab ? activeTab : undefined}
          onTabChange={showForecastTab ? setActiveTab : undefined}
          selectedYear={showForecastTab ? selectedYear : undefined}
          onYearChange={showForecastTab ? setSelectedYear : undefined}
          dayStart={showForecastTab ? yearTabDayRange.start : undefined}
          dayEnd={showForecastTab ? yearTabDayRange.end : undefined}
          onDayRangeChange={showForecastTab ? setYearTabDayRange : undefined}
          dateRange={dateRange}
          setDateRange={setDateRange}
          navigateDateRange={navigateDateRange}
          selfSetOnly={selfSetOnly}
          onSelfSetToggle={setSelfSetOnly}
        />
      </div>

      {/* Tab content: Performance (config-driven sections) or Forecast */}
      {activeTab === "forecast" && showForecastTab ? (
        <div className="animate-rise animate-rise-2">
          <ForecastTab
            forecastData={forecastData}
            kpis={kpis}
            organizationId={currentOrganization?.id}
            canEdit={!hasTunnelVision}
          />
        </div>
      ) : activeTab === "year" && showForecastTab ? (
        <div className="animate-rise animate-rise-2">
          <YearOverviewTab
            organizationId={currentOrganization?.id}
            year={selectedYear}
            dayStart={yearTabDayRange.start}
            dayEnd={yearTabDayRange.end}
          />
        </div>
      ) : useBentoGrid ? (
        /* ── Bento Grid: Asymmetric layout for exec/manager ──────────── */
        <div className="bento-grid animate-rise animate-rise-2">
          {/* Phase 0: Hero renders first frame — instant visual feedback */}
          <div className="bento-hero">{sectionMap.hero}</div>
          {/* Phase 1: Funnel + metrics — next frame */}
          {renderPhase >= 1 && (
            <div className="bento-funnel-metrics">
              <div className="bento-funnel">{sectionMap.funnelHealth}</div>
              <div className="bento-metrics">{sectionMap.salesPerformance}</div>
            </div>
          )}
          {/* Phase 2: Leaderboards — frame after */}
          {renderPhase >= 2 && (
            <div className="bento-leaderboards">
              <div className="liquid-glass anim-lift p-4 flex flex-col">
                {sectionMap.leaderboardCloser}
                <div className="border-t border-black/[0.06] mt-3 pt-3">
                  {sectionMap.perfCloser}
                </div>
              </div>
              <div className="liquid-glass anim-lift p-4 flex flex-col">
                {sectionMap.leaderboardSetter}
                <div className="border-t border-black/[0.06] mt-3 pt-3">
                  {sectionMap.perfSetter}
                </div>
              </div>
              <div className="liquid-glass anim-lift p-4 flex flex-col">
                {sectionMap.leaderboardDialer}
                <div className="border-t border-black/[0.06] mt-3 pt-3">
                  {sectionMap.perfDialer}
                </div>
              </div>
            </div>
          )}
          {/* Phase 3: Activity zone — final frame */}
          {renderPhase >= 3 && (
            <div className="bento-activity-grid">
              <div className="bento-activity-left">
                {sectionMap.transactions}
                {sectionMap.installments}
              </div>
              <div className="bento-activity-right">{sectionMap.upcomingCalls}</div>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Config-driven sections: roleConfig[role].dashboard controls order and visibility */}
          {sections.map((key, idx) => (
            <div key={key} className={`animate-rise animate-rise-${Math.min(idx + 2, 9)}`}>
              {sectionMap[key]}
            </div>
          ))}
        </>
      )}

      {/* Ascensions modal — always mounted, visibility controlled by state */}
      <AscensionsModal
        isOpen={showAscensions}
        onClose={() => setShowAscensions(false)}
        leads={ascensionLeads}
        timezone={timezone}
      />

      {/* Show-up Rate breakdown modal (HP-A) — always mounted, visibility controlled by state */}
      <ShowRateBreakdownModal
        isOpen={showBreakdownOpen}
        onClose={() => setShowBreakdownOpen(false)}
        organizationId={currentOrganization?.id}
        dateRange={dateRange}
        orgTz={currentOrganization?.timezone}
      />
    </div>
  );
};

export default OperationsDashboard;
